import { useSetAtom, useAtom } from "jotai";
import { useRouter, useRouterState } from "@tanstack/react-router";
import type { Goal, HealthArea, Routine } from "instantdb/types";
import { useUser } from "@/hooks/useUser";
import { registerDialog, proDialog } from "@/state/dialogs";
import { routineSuggestionLoadingAtom } from "@/state/routineLoading";
import { goalSuggestionLoadingAtom } from "@/state/goalsLoading";
import { useRoutines } from "@/hooks/routines/useRoutines";
import { useNotification } from "@/hooks/useNotification";
import { suggestGoal } from "@/lib/aiFunctions/suggest-goal";
import { upsertGoal } from "instantdb/client/goal";
import { useGoals } from "@/hooks/goals/useGoals";
import { suggestRoutine } from "@/lib/aiFunctions/suggest-routine";
import { upsertRoutine } from "instantdb/client/routine";
import { useTranslation } from "@/hooks/useTranslation";

type GoalsReturn = {
  healthAreaAIGoals: Goal[];
  healthAreaAIRoutines: Routine[];
  handleSuggestGoal: (healthArea: HealthArea) => void;
  handleSuggestRoutine: (healthArea: HealthArea) => void;
  goalSuggestionLoading: boolean;
  routineSuggestionLoading: boolean;
};

export const useAIHealthArea = (healthArea: HealthArea): GoalsReturn => {
  const router = useRouter();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { isAuthenticated, language, userId, hasFreeAISuggestions, refetchUser } = useUser();
  const setRegisterDialogOpen = useSetAtom(registerDialog);
  const setProDialogOpen = useSetAtom(proDialog);
  const { goals, hasFreeGoals } = useGoals();
  const { routines, hasFreeRoutines } = useRoutines();
  const { showNotification } = useNotification();
  const { t } = useTranslation();

  // Filter AI routines to only show those for this health area
  const healthAreaAIGoals = goals.filter((goal) => goal.healthArea?.id === healthArea.id);

  const healthAreaAIRoutines = routines.filter(
    (routine) => routine.healthArea?.id === healthArea.id,
  );

  // Use separate atoms for routine and goal loading states
  const [routineLoading, setRoutineLoading] = useAtom(routineSuggestionLoadingAtom);
  const [goalLoading, setGoalLoading] = useAtom(goalSuggestionLoadingAtom);

  const healthAreaRoutineLoading = routineLoading[healthArea.id] ?? false;
  const healthAreaGoalLoading = goalLoading[healthArea.id] ?? false;

  const setHealthAreaRoutineLoading = (loading: boolean) => {
    setRoutineLoading({
      ...routineLoading,
      [healthArea.id]: loading,
    });
  };

  const setHealthAreaGoalLoading = (loading: boolean) => {
    setGoalLoading({
      ...goalLoading,
      [healthArea.id]: loading,
    });
  };

  const handleSuggestGoal = async (healthArea: HealthArea) => {
    if (!isAuthenticated) {
      setRegisterDialogOpen(true);
      return;
    }

    if (!hasFreeGoals || !hasFreeAISuggestions) {
      setProDialogOpen(true);
      return;
    }

    // navigate to healthArea if not already there
    if (pathname !== `/dashboard/health-area/${healthArea.id}`) {
      router.navigate({ to: `/dashboard/health-area/${healthArea.id}` });
    }

    setHealthAreaGoalLoading(true);

    const { suggestedGoal } = await suggestGoal({
      data: {
        userId: userId as string,
        healthArea: {
          ...healthArea,
          title: t(healthArea.title),
          description: t(healthArea.description),
        },
        language: language as string,
      },
    }).catch((error) => {
      console.error("Error suggesting goal:", error);
      setHealthAreaGoalLoading(false);
      showNotification({
        type: "error",
        message: "Failed to suggest goal",
        description: "Please try again or create a goal manually.",
      });
    });

    refetchUser();

    if (!suggestedGoal) {
      throw new Error("No goal suggestion received");
    }

    const goalWithId = {
      ...suggestedGoal,
      userId,
      healthArea: { id: healthArea.id },
    };

    setHealthAreaGoalLoading(false);

    await upsertGoal(goalWithId);
    return goalWithId;
  };

  const handleSuggestRoutine = async (healthArea: HealthArea) => {
    if (!isAuthenticated) {
      setRegisterDialogOpen(true);
      return;
    }

    if (!hasFreeRoutines || !hasFreeAISuggestions) {
      setProDialogOpen(true);
      return;
    }

    // navigate to healthArea if not already there
    if (pathname !== `/dashboard/health-area/${healthArea.id}`) {
      router.navigate({ to: `/dashboard/health-area/${healthArea.id}` });
    }

    setHealthAreaRoutineLoading(true);

    const { suggestedRoutine } = await suggestRoutine({
      data: {
        userId: userId as string,
        healthArea: {
          ...healthArea,
          title: t(healthArea.title),
          description: t(healthArea.description),
        },
        language: language as string,
      },
    }).catch((error) => {
      console.error("Error suggesting routine:", error);
      setHealthAreaRoutineLoading(false);
      showNotification({
        type: "error",
        message: "Failed to suggest routine",
        description: "Please try again or create a routine manually.",
      });
    });

    refetchUser();

    if (!suggestedRoutine) {
      throw new Error("No routine suggestion received");
    }

    const routineWithId = {
      ...suggestedRoutine,
      userId,
      healthArea: { id: healthArea.id },
    };

    setHealthAreaRoutineLoading(false);

    await upsertRoutine(routineWithId);
    return routineWithId;
  };

  return {
    healthAreaAIGoals,
    healthAreaAIRoutines,
    handleSuggestGoal,
    handleSuggestRoutine,
    goalSuggestionLoading: healthAreaGoalLoading,
    routineSuggestionLoading: healthAreaRoutineLoading,
  };
};
